// ReceiptsPage.js
// Receipt document ledger — tool repairs now, consumables/job costing later.
// Source: tagout_log where receipt_photo_url IS NOT NULL (or all returned records for the no-receipt filter)
// Exports: Excel (SheetJS) + PDF (jsPDF)

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { getCompanyName } from '../lib/tenant';

// 74z8 - a downloaded file keeps its name for as long as it sits in someone's
// Downloads folder, so the tenant belongs in it rather than a hardcoded
// "DR-Signs". Stripped to filename-safe characters because company_name is
// free text and a slash or colon would break the save on Windows.
function exportFilePrefix() {
  return getCompanyName().replace(/[^A-Za-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'Stede';
}
import { getPhotoUrl } from '../lib/storage';
import StoredImage, { useSignedUrl } from '../components/StoredImage';
import { TOOL_BUCKET } from '../lib/storedFile';
import { useAuth } from '../components/AuthContext';
import { isBranchScopedManager } from '../lib/permissions';

// ── CONSTANTS ──────────────────────────────────────────────
const OUTCOME_BADGE = {
  repaired: { label: 'Repaired',      cls: 'badge-active'   },
  replaced: { label: 'Replaced',      cls: 'badge-pending'  },
  traded:   { label: 'Traded',        cls: 'badge-transit'  },
};

function fmt$(v) {
  if (v == null || v === '') return '—';
  return '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function daysBetween(a, b) {
  if (!a || !b) return null;
  return Math.round((new Date(b) - new Date(a)) / 86400000);
}

// ── SUMMARY BAR ────────────────────────────────────────────
function SummaryBar({ rows }) {
  const withReceipt   = rows.filter(r => r.receipt_photo_url).length;
  const totalSpend    = rows.reduce((s, r) => s + (r.is_warranty ? 0 : (parseFloat(r.actual_repair_cost) || 0)), 0);
  const warrantyCt    = rows.filter(r => r.is_warranty).length;
  const missingCt     = rows.filter(r => !r.receipt_photo_url).length;

  return (
    <div style={{
      display: 'flex', gap: '0', marginBottom: '1rem',
      border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)',
      overflow: 'hidden', background: 'white',
    }}>
      {[
        { label: 'Records shown',       value: rows.length,       color: 'var(--black)'    },
        { label: 'Total spend',         value: fmt$(totalSpend),  color: '#C62828'         },
        { label: 'Warranty / $0',       value: warrantyCt,        color: '#1565C0'         },
        { label: 'No receipt on file',  value: missingCt,         color: missingCt > 0 ? '#E65100' : 'var(--gray-lt)' },
        { label: 'Receipt photos',      value: withReceipt,       color: 'var(--green-dk)' },
      ].map((s, i) => (
        <div key={i} style={{
          flex: 1, padding: '12px 16px', textAlign: 'center',
          borderRight: i < 4 ? '1px solid var(--border)' : 'none',
        }}>
          <div style={{ fontSize: '18px', fontWeight: 700, color: s.color, fontFamily: 'var(--font-head)' }}>{s.value}</div>
          <div style={{ fontSize: '11px', color: 'var(--gray-mid)', marginTop: '2px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{s.label}</div>
        </div>
      ))}
    </div>
  );
}

// ── RECEIPT DETAIL MODAL ───────────────────────────────────
function ReceiptModal({ record, onClose }) {
  // #536. Component scope, so the address can be fetched with a hook and every
  // use below it - the picture, the zoom, the download button - is unchanged.
  const photoUrl = useSignedUrl(record.receipt_photo_url, TOOL_BUCKET);
  const days = daysBetween(record.tag_date, record.return_date);

  function handleDownload() {
    if (!photoUrl) return;
    const a = document.createElement('a');
    a.href = photoUrl;
    a.download = `receipt-${record.tools?.name || 'tool'}-${record.return_date?.slice(0, 10) || 'unknown'}.jpg`;
    a.target = '_blank';
    a.click();
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: '780px', width: '95vw' }}>
        <div className="modal-header">
          <span className="modal-title">Receipt — {record.tools?.name || 'Unknown tool'}</span>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body" style={{ padding: 0 }}>
          <div style={{ display: 'flex', gap: 0, minHeight: '400px' }}>

            {/* LEFT — photo */}
            <div style={{
              flex: '0 0 55%', background: '#F0F0F0', display: 'flex', alignItems: 'center',
              justifyContent: 'center', borderRight: '1px solid var(--border)', padding: '16px',
              minHeight: '360px',
            }}>
              {photoUrl ? (
                <img
                  src={photoUrl}
                  alt="Receipt"
                  style={{ maxWidth: '100%', maxHeight: '480px', objectFit: 'contain', borderRadius: '6px', cursor: 'zoom-in' }}
                  onClick={() => window.open(photoUrl, '_blank')}
                />
              ) : (
                <div style={{ textAlign: 'center', color: 'var(--gray-lt)' }}>
                  <div style={{ fontSize: '40px', marginBottom: '12px' }}>📄</div>
                  <div style={{ fontSize: '14px', fontWeight: 600 }}>No receipt on file</div>
                  <div style={{ fontSize: '12px', marginTop: '4px' }}>
                    Entered {fmtDate(record.return_date)} by {record.returned_by_name || '—'}
                  </div>
                </div>
              )}
            </div>

            {/* RIGHT — metadata */}
            <div style={{ flex: 1, padding: '20px', overflowY: 'auto' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

                {/* Outcome badge */}
                <div>
                  {record.is_warranty
                    ? <span className="badge" style={{ background: '#E3F2FD', color: '#1565C0', fontWeight: 700 }}>WARRANTY — $0.00</span>
                    : <span className={`badge ${OUTCOME_BADGE[record.repair_or_replace]?.cls || 'badge-pending'}`}>
                        {OUTCOME_BADGE[record.repair_or_replace]?.label || record.repair_or_replace}
                      </span>
                  }
                </div>

                {/* Key fields */}
                {[
                  { label: 'Tool',          value: record.tools?.name },
                  { label: 'Branch',        value: record.tools?.branches?.name },
                  { label: 'Model #',       value: record.tools?.model_number },
                  { label: 'Serial #',      value: record.tools?.serial_number },
                  { label: 'Shop',          value: record.shop_name },
                  { label: 'Ticket #',      value: record.ticket_number },
                  { label: 'Tagged',        value: fmtDate(record.tag_date) },
                  { label: 'Returned',      value: fmtDate(record.return_date) },
                  { label: 'Days out',      value: days != null ? `${days} days` : null },
                  { label: 'Tagged by',     value: record.tagged_by_name },
                  { label: 'Returned by',   value: record.returned_by_name },
                  { label: 'Actual cost',   value: record.is_warranty ? '$0.00 (warranty)' : fmt$(record.actual_repair_cost) },
                ].map(({ label, value }) => value ? (
                  <div key={label} style={{ display: 'flex', gap: '8px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--gray-mid)', textTransform: 'uppercase', letterSpacing: '0.04em', width: '90px', flexShrink: 0, paddingTop: '1px' }}>{label}</div>
                    <div style={{ fontSize: '13px', color: 'var(--black)', flex: 1 }}>{value}</div>
                  </div>
                ) : null)}

                {/* Resolution notes */}
                {record.resolution_notes && (
                  <div style={{ paddingTop: '8px', borderTop: '1px solid var(--border)' }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--gray-mid)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>Resolution notes</div>
                    <div style={{ fontSize: '13px', color: 'var(--black)', fontStyle: 'italic', lineHeight: 1.5 }}>"{record.resolution_notes}"</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
          {photoUrl && (
            <button className="btn btn-primary" onClick={handleDownload}>
              ↓ Download receipt
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── PDF EXPORT ─────────────────────────────────────────────
async function exportPDF(rows, filters) {
  // Dynamically import jsPDF to avoid bundle bloat
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const pageW = 210;
  const margin = 14;
  const contentW = pageW - margin * 2;
  let y = margin;

  // ── COVER / HEADER ──
  doc.setFillColor(198, 40, 40); // var(--red)
  doc.rect(0, 0, pageW, 28, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(255, 255, 255);
  doc.text(`${getCompanyName()} — Service Receipts`, margin, 12);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(255, 200, 200);
  const filterDesc = [
    filters.branchName ? `Branch: ${filters.branchName}` : 'All branches',
    filters.outcome    ? `Outcome: ${filters.outcome}`    : 'All outcomes',
    filters.dateFrom || filters.dateTo
      ? `${filters.dateFrom || 'start'} to ${filters.dateTo || 'today'}`
      : 'All dates',
  ].join('  |  ');
  doc.text(filterDesc, margin, 19);
  doc.setTextColor(255, 255, 255);
  doc.text(`Generated ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}  -  ${rows.length} records`, margin, 25);

  y = 36;

  // ── SUMMARY ROW ──
  const totalSpend = rows.reduce((s, r) => s + (r.is_warranty ? 0 : (parseFloat(r.actual_repair_cost) || 0)), 0);
  const warrantyCt = rows.filter(r => r.is_warranty).length;
  const withPhoto  = rows.filter(r => r.receipt_photo_url).length;

  doc.setFillColor(245, 245, 245);
  doc.rect(margin, y, contentW, 14, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(60, 60, 60);

  const cols = [
    { label: 'TOTAL RECORDS', value: String(rows.length),                   x: margin + 4       },
    { label: 'TOTAL SPEND',   value: fmt$(totalSpend),                      x: margin + 44      },
    { label: 'WARRANTY / $0', value: String(warrantyCt),                    x: margin + 90      },
    { label: 'RECEIPT PHOTOS',value: `${withPhoto} of ${rows.length}`,      x: margin + 128     },
  ];
  cols.forEach(c => {
    doc.setFontSize(6); doc.setFont('helvetica', 'normal'); doc.setTextColor(120, 120, 120);
    doc.text(c.label, c.x, y + 5);
    doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(30, 30, 30);
    doc.text(c.value, c.x, y + 11);
  });

  y += 20;

  // ── RECORDS — one per record, photo + metadata side by side ──
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    const photoUrl = r.receipt_photo_url ? await getPhotoUrl(r.receipt_photo_url) : null;
    const days = daysBetween(r.tag_date, r.return_date);

    // Section divider
    doc.setFillColor(198, 40, 40);
    doc.rect(margin, y, contentW, 5, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(255, 255, 255);
    doc.text(`${i + 1}. ${r.tools?.name || 'Unknown tool'} — ${r.tools?.branches?.name || '—'}`, margin + 2, y + 3.5);

    const outcomeLabel = r.is_warranty ? 'WARRANTY' : (OUTCOME_BADGE[r.repair_or_replace]?.label || r.repair_or_replace || '—');
    doc.text(outcomeLabel, pageW - margin - 2, y + 3.5, { align: 'right' });
    y += 8;

    const blockTop = y;
    const photoW = 72;
    const metaX = margin + photoW + 6;
    const metaW = contentW - photoW - 6;

    // Photo column
    if (photoUrl) {
      try {
        const imgData = await fetchImageAsBase64(photoUrl);
        if (imgData) {
          doc.addImage(imgData, 'JPEG', margin, blockTop, photoW, 52, undefined, 'FAST');
        } else {
          noPhotoBox(doc, margin, blockTop, photoW, 52);
        }
      } catch {
        noPhotoBox(doc, margin, blockTop, photoW, 52);
      }
    } else {
      noPhotoBox(doc, margin, blockTop, photoW, 52);
    }

    // Metadata column
    const meta = [
      ['Model #',      r.tools?.model_number],
      ['Serial #',     r.tools?.serial_number],
      ['Shop',         r.shop_name],
      ['Ticket #',     r.ticket_number],
      ['Tagged',       fmtDate(r.tag_date)],
      ['Returned',     fmtDate(r.return_date)],
      ['Days out',     days != null ? `${days} days` : null],
      ['Tagged by',    r.tagged_by_name],
      ['Returned by',  r.returned_by_name],
      ['Actual cost',  r.is_warranty ? '$0.00 (warranty)' : fmt$(r.actual_repair_cost)],
    ].filter(([, v]) => v);

    let ym = blockTop + 4;
    meta.forEach(([label, value]) => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(120, 120, 120);
      doc.text(label, metaX, ym);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(20, 20, 20);
      doc.text(String(value), metaX + 24, ym);
      ym += 4.8;
    });

    // Resolution notes (truncated to 2 lines)
    if (r.resolution_notes) {
      doc.setFont('helvetica', 'bolditalic');
      doc.setFontSize(6.5);
      doc.setTextColor(80, 80, 80);
      const lines = doc.splitTextToSize(`"${r.resolution_notes}"`, metaW);
      doc.text(lines.slice(0, 2), metaX, ym + 1);
    }

    y = blockTop + 56;

    // Page break — check before next block starts (each block needs ~65mm)
    if (i < rows.length - 1 && y + 65 > 287) {
      doc.addPage();
      y = margin;
    }
  }

  // Save
  const dateStr = new Date().toISOString().slice(0, 10);
  doc.save(`${exportFilePrefix()}-Receipts-${dateStr}.pdf`);
}

function noPhotoBox(doc, x, y, w, h) {
  doc.setFillColor(240, 240, 240);
  doc.rect(x, y, w, h, 'F');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(160, 160, 160);
  doc.text('No receipt on file', x + w / 2, y + h / 2, { align: 'center' });
}

async function fetchImageAsBase64(url) {
  try {
    const resp = await fetch(url);
    if (!resp.ok) return null;
    const blob = await resp.blob();
    return await new Promise((res, rej) => {
      const reader = new FileReader();
      reader.onloadend = () => res(reader.result);
      reader.onerror = rej;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

// ── EXCEL EXPORT ───────────────────────────────────────────
async function exportExcel(rows) {
  const XLSX = await import('xlsx');

  const data = rows.map(r => ({
    'Return Date':         r.return_date ? new Date(r.return_date).toLocaleDateString() : '',
    'Tool Name':           r.tools?.name || '',
    'Model #':             r.tools?.model_number || '',
    'Serial #':            r.tools?.serial_number || '',
    'Branch':              r.tools?.branches?.name || '',
    'Department':          r.tools?.department || '',
    'Outcome':             r.is_warranty ? 'warranty' : (r.repair_or_replace || ''),
    'Ticket #':            r.ticket_number || '',
    'Shop Name':           r.shop_name || '',
    'Actual Cost ($)':     r.is_warranty ? 0 : (parseFloat(r.actual_repair_cost) || ''),
    'Warranty':            r.is_warranty ? 'Yes' : 'No',
    'Receipt on File':     r.receipt_photo_url ? 'Yes' : 'No',
    'Tag Reason':          r.reason || '',
    'Issue Description':   r.issue_description || '',
    'Resolution Notes':    r.resolution_notes || '',
    'Tagged By':           r.tagged_by_name || '',
    'Returned By':         r.returned_by_name || '',
    'Tag Date':            r.tag_date ? new Date(r.tag_date).toLocaleDateString() : '',
    'Days in Repair':      daysBetween(r.tag_date, r.return_date) ?? '',
    'Est. Cost ($)':       parseFloat(r.estimated_repair_cost) || '',
  }));

  const ws = XLSX.utils.json_to_sheet(data);

  // Column widths
  ws['!cols'] = [
    { wch: 14 }, { wch: 26 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 14 },
    { wch: 12 }, { wch: 12 }, { wch: 20 }, { wch: 14 }, { wch: 10 }, { wch: 14 },
    { wch: 18 }, { wch: 30 }, { wch: 36 }, { wch: 18 }, { wch: 18 }, { wch: 14 },
    { wch: 14 }, { wch: 14 },
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Service Receipts');

  // Add a summary sheet
  const summary = [
    [`${getCompanyName()} — Service Receipts Export`],
    [`Generated: ${new Date().toLocaleDateString()}`],
    [`Total records: ${rows.length}`],
    [`Total spend: ${fmt$(rows.reduce((s, r) => s + (r.is_warranty ? 0 : (parseFloat(r.actual_repair_cost) || 0)), 0))}`],
    [`Warranty claims: ${rows.filter(r => r.is_warranty).length}`],
    [`Receipts on file: ${rows.filter(r => r.receipt_photo_url).length} of ${rows.length}`],
    [],
    ['Spend by outcome:'],
    ...['repaired', 'replaced', 'traded'].map(outcome => {
      const subset = rows.filter(r => r.repair_or_replace === outcome && !r.is_warranty);
      return [
        outcome.charAt(0).toUpperCase() + outcome.slice(1),
        subset.length + ' records',
        fmt$(subset.reduce((s, r) => s + (parseFloat(r.actual_repair_cost) || 0), 0)),
      ];
    }),
  ];
  const ws2 = XLSX.utils.aoa_to_sheet(summary);
  ws2['!cols'] = [{ wch: 28 }, { wch: 18 }, { wch: 18 }];
  XLSX.utils.book_append_sheet(wb, ws2, 'Summary');

  const dateStr = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `${exportFilePrefix()}-Receipts-${dateStr}.xlsx`);
}

// ── MAIN RECEIPTS PAGE ─────────────────────────────────────
export default function ReceiptsPage() {
  const { profile, isAdmin, isManager, isExecutive } = useAuth();
  const isBranchScoped = isBranchScopedManager({ isAdmin, isManager, isExecutive });

  // Sub-tab: 'repairs' | 'consumables'
  const [subTab, setSubTab] = useState('repairs');

  // Data
  const [records, setRecords] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [dateFrom, setDateFrom]     = useState('');
  const [dateTo, setDateTo]         = useState('');
  const [filterBranch, setFilterBranch] = useState('');
  const [filterOutcome, setFilterOutcome] = useState('');
  const [filterReceipt, setFilterReceipt] = useState('');

  // UI state
  const [search, setSearch]         = useState('');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [exporting, setExporting]   = useState(false);
  const [exportMsg, setExportMsg]   = useState('');

  // Default date range: current month
  useEffect(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    setDateFrom(firstDay.toISOString().slice(0, 10));
    setDateTo(now.toISOString().slice(0, 10));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);

    let q = supabase
      .from('tagout_log')
      .select('*, tools!inner(id, name, model_number, serial_number, department, branch_id, branches(name))')
      .eq('status', 'Returned')
      .order('return_date', { ascending: false });

    // Branch scoping for non-admins
    if (isBranchScoped && profile?.branch_id) {
      q = q.eq('tools.branch_id', profile.branch_id);
    }

    const { data, error } = await q;
    if (!error) setRecords(data || []);

    const { data: br } = await supabase.from('branches').select('*').order('name');
    setBranches(br || []);

    setLoading(false);
  }, [isBranchScoped, profile?.branch_id]);

  useEffect(() => { load(); }, [load]);

  // ── CLIENT-SIDE FILTERING ──
  const filtered = records.filter(r => {
    // Date range (on return_date)
    if (dateFrom && r.return_date && r.return_date.slice(0, 10) < dateFrom) return false;
    if (dateTo   && r.return_date && r.return_date.slice(0, 10) > dateTo)   return false;

    // Branch
    if (filterBranch && String(r.tools?.branch_id) !== filterBranch) return false;

    // Outcome (include warranty as its own bucket)
    if (filterOutcome === 'warranty' && !r.is_warranty)                      return false;
    if (filterOutcome && filterOutcome !== 'warranty' && r.repair_or_replace !== filterOutcome) return false;

    // Receipt status
    if (filterReceipt === 'has'     &&  !r.receipt_photo_url) return false;
    if (filterReceipt === 'missing' &&   r.receipt_photo_url) return false;

    // Search
    if (search) {
      const q = search.toLowerCase();
      const haystack = [
        r.tools?.name, r.tools?.model_number, r.tools?.serial_number,
        r.shop_name, r.ticket_number, r.tagged_by_name, r.returned_by_name,
        r.resolution_notes, r.reason,
      ].filter(Boolean).join(' ').toLowerCase();
      if (!haystack.includes(q)) return false;
    }

    return true;
  });

  // ── EXPORT HANDLERS ──
  async function handleExcelExport() {
    setExporting(true);
    setExportMsg('Building spreadsheet…');
    try {
      await exportExcel(filtered);
      setExportMsg('');
    } catch (e) {
      setExportMsg('Export failed: ' + e.message);
    }
    setExporting(false);
  }

  async function handlePDFExport() {
    setExporting(true);
    setExportMsg(`Building PDF — fetching ${filtered.filter(r => r.receipt_photo_url).length} receipt image(s)…`);
    try {
      await exportPDF(filtered, {
        branchName: filterBranch ? (branches.find(b => String(b.id) === filterBranch)?.name || '') : '',
        outcome: filterOutcome,
        dateFrom,
        dateTo,
      });
      setExportMsg('');
    } catch (e) {
      setExportMsg('PDF export failed: ' + e.message);
    }
    setExporting(false);
  }

  // ── RENDER ──
  return (
    <div>
      {/* PAGE HEADER */}
      <div className="page-header">
        <div className="page-title">Receipts</div>
        <div className="page-sub">Document ledger — service receipts, invoices, and records</div>
      </div>

      {/* SUB-TABS */}
      <div style={{
        display: 'flex', gap: 0, marginBottom: '1.25rem',
        borderBottom: '2px solid #E8E8E8',
      }}>
        {[
          { id: 'repairs',      label: 'Tool repairs',              active: true  },
          { id: 'consumables',  label: 'Consumables & inventory',   active: false },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => tab.active && setSubTab(tab.id)}
            style={{
              padding: '10px 16px',
              fontSize: '12px',
              fontWeight: subTab === tab.id ? 700 : 500,
              color: !tab.active
                ? 'var(--gray-lt)'
                : subTab === tab.id
                  ? 'var(--green-dk)'
                  : 'var(--gray-mid)',
              borderBottom: subTab === tab.id ? '2px solid var(--green-dk)' : '2px solid transparent',
              background: 'none', border: 'none', cursor: tab.active ? 'pointer' : 'not-allowed',
              marginBottom: '-2px', whiteSpace: 'nowrap',
              fontFamily: 'var(--font-head)', textTransform: 'uppercase', letterSpacing: '0.3px',
            }}
          >
            {tab.label}
            {!tab.active && (
              <span style={{
                marginLeft: '6px', fontSize: '10px', padding: '2px 6px',
                background: '#F0F0F0', color: 'var(--gray-lt)',
                borderRadius: '10px', fontWeight: 600, textTransform: 'none', letterSpacing: 0,
              }}>
                Coming soon
              </span>
            )}
          </button>
        ))}
      </div>

      {/* CONSUMABLES PLACEHOLDER */}
      {subTab === 'consumables' && (
        <div className="card">
          <div className="empty-state" style={{ padding: '48px 24px' }}>
            <div className="empty-state-icon">🧾</div>
            <div className="empty-state-title">Consumables & inventory receipts</div>
            <div className="empty-state-sub" style={{ maxWidth: '400px' }}>
              This tab will house purchase receipts and invoices for consumables, materials,
              and inventory items as Stede expands into full inventory
              and job costing management.
            </div>
          </div>
        </div>
      )}

      {/* TOOL REPAIRS TAB */}
      {subTab === 'repairs' && (
        <>
          {/* FILTERS */}
          <div className="toolbar" style={{ flexWrap: 'wrap', gap: '8px', marginBottom: '1rem' }}>
            {/* Date range */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <input
                type="date" className="filter-select"
                value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                style={{ minWidth: '130px' }}
              />
              <span style={{ fontSize: '12px', color: 'var(--gray-lt)' }}>→</span>
              <input
                type="date" className="filter-select"
                value={dateTo} onChange={e => setDateTo(e.target.value)}
                style={{ minWidth: '130px' }}
              />
            </div>

            {/* Branch */}
            {(isAdmin || isExecutive) && (
              <select className="filter-select" value={filterBranch} onChange={e => setFilterBranch(e.target.value)}>
                <option value="">All branches</option>
                {branches.map(b => <option key={b.id} value={String(b.id)}>{b.name}</option>)}
              </select>
            )}

            {/* Outcome */}
            <select className="filter-select" value={filterOutcome} onChange={e => setFilterOutcome(e.target.value)}>
              <option value="">All outcomes</option>
              <option value="repaired">Repaired</option>
              <option value="replaced">Replaced</option>
              <option value="traded">Traded</option>
              <option value="warranty">Warranty</option>
            </select>

            {/* Receipt status */}
            <select className="filter-select" value={filterReceipt} onChange={e => setFilterReceipt(e.target.value)}>
              <option value="">All records</option>
              <option value="has">Has receipt photo</option>
              <option value="missing">No receipt on file</option>
            </select>

            {/* Search */}
            <input
              className="search-input"
              placeholder="Search tool, shop, ticket…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ flex: 1, minWidth: '160px' }}
            />

            {/* Export buttons */}
            <div style={{ display: 'flex', gap: '6px', marginLeft: 'auto' }}>
              <button
                className="btn btn-ghost btn-sm"
                onClick={handleExcelExport}
                disabled={exporting || filtered.length === 0}
                title="Export filtered records to Excel"
              >
                ↓ Excel
              </button>
              <button
                className="btn btn-ghost btn-sm"
                onClick={handlePDFExport}
                disabled={exporting || filtered.length === 0}
                title="Export filtered records to PDF with receipt photos"
              >
                ↓ PDF
              </button>
            </div>
          </div>

          {/* Export status */}
          {exportMsg && (
            <div style={{
              marginBottom: '12px', padding: '10px 14px', background: '#F0F4FF',
              border: '1px solid #C7D2FE', borderRadius: 'var(--radius-lg)',
              fontSize: '12px', color: '#3730A3', display: 'flex', alignItems: 'center', gap: '8px',
            }}>
              <div className="spinner" style={{ width: '14px', height: '14px', borderWidth: '2px' }} />
              {exportMsg}
            </div>
          )}

          {/* SUMMARY BAR */}
          {!loading && <SummaryBar rows={filtered} />}

          {/* TABLE */}
          {loading ? (
            <div className="loading"><div className="spinner" />Loading receipts…</div>
          ) : (
            <div className="card">
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Tool</th>
                      <th>Branch</th>
                      <th>Outcome</th>
                      <th>Ticket #</th>
                      <th>Shop</th>
                      <th>Actual cost</th>
                      <th>Days out</th>
                      <th>Receipt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 ? (
                      <tr><td colSpan="9">
                        <div className="empty-state">
                          <div className="empty-state-icon">🧾</div>
                          <div className="empty-state-title">No records match your filters</div>
                          <div className="empty-state-sub">Try expanding the date range or clearing filters.</div>
                        </div>
                      </td></tr>
                    ) : filtered.map(r => {
                      const days = daysBetween(r.tag_date, r.return_date);
                      const hasPhoto = !!r.receipt_photo_url;

                      return (
                        <tr
                          key={r.id}
                          onClick={() => setSelectedRecord(r)}
                          style={{ cursor: 'pointer' }}
                          className="clickable-row"
                        >
                          <td className="text-muted" style={{ whiteSpace: 'nowrap' }}>
                            {fmtDate(r.return_date)}
                          </td>
                          <td>
                            <strong>{r.tools?.name || '—'}</strong>
                            {r.tools?.model_number && (
                              <div style={{ fontSize: '11px', color: 'var(--gray-mid)' }}>{r.tools.model_number}</div>
                            )}
                          </td>
                          <td className="text-muted">{r.tools?.branches?.name || '—'}</td>
                          <td>
                            {r.is_warranty
                              ? <span className="badge" style={{ background: '#E3F2FD', color: '#1565C0', fontSize: '11px' }}>Warranty</span>
                              : <span className={`badge ${OUTCOME_BADGE[r.repair_or_replace]?.cls || 'badge-pending'}`} style={{ fontSize: '11px' }}>
                                  {OUTCOME_BADGE[r.repair_or_replace]?.label || r.repair_or_replace || '—'}
                                </span>
                            }
                          </td>
                          <td className="text-muted">{r.ticket_number || '—'}</td>
                          <td className="text-muted">{r.shop_name || '—'}</td>
                          <td style={{ fontWeight: 600, color: r.is_warranty ? '#1565C0' : 'var(--black)' }}>
                            {r.is_warranty ? '$0.00' : fmt$(r.actual_repair_cost)}
                          </td>
                          <td className="text-muted">{days != null ? `${days}d` : '—'}</td>
                          <td>
                            {hasPhoto ? (
                              <StoredImage
                                value={r.receipt_photo_url}
                                bucket={TOOL_BUCKET}
                                alt="Receipt thumbnail"
                                style={{
                                  width: '44px', height: '34px', objectFit: 'cover',
                                  borderRadius: '4px', border: '1px solid var(--border)',
                                  display: 'block',
                                }}
                              />
                            ) : (
                              <span style={{ fontSize: '11px', color: 'var(--gray-lt)', fontStyle: 'italic' }}>
                                None
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* DETAIL MODAL */}
      {selectedRecord && (
        <ReceiptModal
          record={selectedRecord}
          onClose={() => setSelectedRecord(null)}
        />
      )}
    </div>
  );
}